import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { BookMarked } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Login() {
    const { t } = useTranslation();
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email.trim().toLowerCase(), password);
        setLoading(false);
        if (res.ok) {
            toast.success(t("auth.toast_welcome_back"));
            navigate("/biblioteca");
        } else {
            toast.error(res.error);
        }
    };

    return (
        <main className="min-h-[calc(100vh-73px)] flex items-center justify-center px-5 py-12 md:py-16 bg-[var(--bg)]">
            <section className="w-full max-w-md">
                <Link
                    to="/"
                    data-testid="login-brand-link"
                    className="flex items-center justify-center gap-2 text-[var(--ink)] mb-8"
                >
                    <BookMarked size={18} strokeWidth={1.5} />
                    <span
                        className="font-display text-2xl tracking-tight"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("nav.brand")}
                    </span>
                </Link>

                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3 text-center">
                    {t("auth.login_eyebrow")}
                </p>
                <h1
                    className="font-display text-4xl sm:text-5xl text-[var(--ink)] text-center mb-3 tracking-tight"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96' }}
                >
                    {t("auth.login_title")}
                </h1>
                <p className="text-center text-sm text-[var(--ink-muted)] mb-10 max-w-xs mx-auto">
                    {t("auth.login_brand_subline")}
                </p>

                <GoogleAuthButton />
                <p className="text-center text-[10px] uppercase tracking-widest text-[var(--ink-dim)] mt-2">
                    {t("auth.google_safe_redirect")}
                </p>

                <div className="flex items-center gap-4 my-8">
                    <div className="h-px flex-1 bg-[var(--border)]" />
                    <span className="text-xs uppercase tracking-widest text-[var(--ink-dim)]">
                        {t("auth.or_email")}
                    </span>
                    <div className="h-px flex-1 bg-[var(--border)]" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                            {t("auth.email")}
                        </label>
                        <input
                            data-testid="login-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                            {t("auth.password")}
                        </label>
                        <input
                            data-testid="login-password-input"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                        />
                    </div>
                    <button
                        data-testid="login-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3.5 text-sm uppercase tracking-widest mt-4"
                    >
                        {loading ? t("auth.submit_login_loading") : t("auth.submit_login")}
                    </button>
                </form>
                <p className="text-center text-sm text-[var(--ink-muted)] mt-6">
                    <Link
                        to="/forgot-password"
                        data-testid="login-forgot-link"
                        className="text-[var(--ink-muted)] underline underline-offset-4 hover:text-[var(--accent)]"
                    >
                        {t("auth.forgot_password")}
                    </Link>
                </p>
                <p className="text-center text-sm text-[var(--ink-muted)] mt-4">
                    {t("auth.no_account")}{" "}
                    <Link
                        to="/registrarse"
                        data-testid="go-to-register-link"
                        className="text-[var(--ink)] underline underline-offset-4 hover:text-[var(--accent)]"
                    >
                        {t("auth.go_register")}
                    </Link>
                </p>
            </section>
        </main>
    );
}
